import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import SelectorPaso from "./SelectorPaso";
import NotionSelector from "./NotionSelector";
import SelectorIncidencia from "./SelectorIncidencia";
import SelectorReunion from "./SelectorReunion";
import SelectorTareaUnico from "../misTareas/SelectorTareaUnico";

// A QUE esta asignada una entrada del cuaderno: un campo, y un tipo que decide
// contra que se busca.
//
// Antes eran tres campos sueltos —paso del proyecto, pagina de Notion e
// incidencia— y solo uno podia estar puesto, asi que dos de los tres estaban
// siempre vacios ocupando sitio. Con un tipo delante se lee de un tiron: "esto
// era de la incidencia HT03442", sin importar de que clase sea la asignacion.
//
// DEBAJO no cambia nada. Se siguen escribiendo las mismas columnas —task_id y
// project_id, external_task_id y external_project_id, incidencia_id y
// incidencia_ref— y eso es a proposito:
//
//   · `task_id` y `project_id` son claves ajenas de verdad, con ON DELETE SET
//     NULL. Guardar en su lugar un (tipo, referencia) de texto perderia eso: al
//     borrar un paso quedaria un id colgando, y el aviso de "el paso enlazado ya
//     no esta" no tendria forma de saberlo.
//   · GET /api/proyectos/:id/tiempo suma las horas reales mirando LOS DOS pares
//     a la vez (a.project_id y a.external_project_id). Con una sola columna
//     polimorfica habria que filtrar por texto y castear en cada consulta.
//
// Asi que la union es de presentacion. Es donde estaba el problema.

// Las pestañas son FUENTES donde buscar, y "sin asignar" no es una fuente: es
// el buscador vacio. Cuando estaba como pestaña, marcarla dejaba el campo sin
// buscador y con una frase explicando que no habia nada — para asignar algo
// habia que adivinar que las pestañas eran el camino. Un callejon sin salida.
const TIPOS = [
  { key: "paso",       label: "Paso de proyecto", icon: "diagram-3" },
  { key: "outlook",    label: "Reunión",          icon: "calendar-week" },
  { key: "notion",     label: "Notion",           icon: "file-earmark-text" },
  { key: "incidencia", label: "Incidencia",       icon: "headset" },
];

// Vaciar los OTROS enlaces al cambiar de tipo. Una entrada colgada de dos sitios
// a la vez contaria sus horas dos veces en el tiempo real del proyecto.
const SOLO = {
  paso:       { external_task_id: null, external_project_id: null, mis_tarea_id: null,
                incidencia_id: null, incidencia_ref: null, incidencia_titulo: null, outlook_event_id: null },
  outlook:    { task_id: null, project_id: null, external_task_id: null, mis_tarea_id: null,
                external_project_id: null, incidencia_id: null, incidencia_ref: null, incidencia_titulo: null },
  notion:     { task_id: null, project_id: null, mis_tarea_id: null,
                incidencia_id: null, incidencia_ref: null, incidencia_titulo: null, outlook_event_id: null },
  incidencia: { task_id: null, project_id: null, external_task_id: null, mis_tarea_id: null,
                external_project_id: null, outlook_event_id: null },
  // «Tarea» (selector unico): paso, pagina de Notion o encargo. Cambiar a ella suelta lo de los otros dos.
  tarea:      { incidencia_id: null, incidencia_ref: null, incidencia_titulo: null, outlook_event_id: null },
};
// Los tres enlaces que caben en «Tarea»: solo uno puesto a la vez.
const SIN_TAREA = { task_id: null, project_id: null, external_task_id: null, external_project_id: null, mis_tarea_id: null };

// Con el selector unico, paso y Notion son UNA fuente («Tarea»); reunion e incidencia conservan la suya.
const TIPOS_UNICO = [
  { key: "tarea",      label: "Tarea",      icon: "list-check",    desc: "Paso de proyecto, Notion o tarea enviada" },
  { key: "outlook",    label: "Reunión",    icon: "calendar-week", desc: "Una reunión de tu calendario de Outlook" },
  { key: "incidencia", label: "Incidencia", icon: "headset",       desc: "Un ticket del panel de incidencias" },
];

// `id` y `aria-labelledby` los pone <Campo>: el grupo queda nombrado por la etiqueta
// «Asignación» que tiene al lado.
export default function SelectorAsignacion({
  entry, misPasos = [], notionItems = [], verIncidencias = false,
  onChange, onAbrirIncidencia, id, "aria-labelledby": labelledBy,
  // Origen con el que se abre un campo SIN enlace (p. ej. el predeterminado del
  // tipo de entrada): "paso" | "outlook" | "notion" | "incidencia" | null. Si esa
  // persona no puede usarlo, se ignora y vale el primero disponible.
  origenInicial = null,
  // Lo que hay en Mis tareas (aplanado). Null = la persona no lo ve: se mantiene el formulario de siempre,
  // con sus cuatro fuentes.
  poolMisTareas = null,
}) {
  const a = entry;
  const unico = poolMisTareas !== null;
  const TIPOS_USO = unico ? TIPOS_UNICO : TIPOS;

  // El enlace que tiene la entrada, deducido de sus columnas. No se guarda un
  // campo "tipo": seria decir lo que las columnas ya dicen, y solo podria
  // desincronizarse.
  const enlazado = unico && (a.task_id || a.external_task_id || a.mis_tarea_id) ? "tarea"
    : a.task_id ? "paso"
    : a.external_task_id ? "notion"
    : a.incidencia_id ? "incidencia"
    : a.outlook_event_id ? "outlook"
    : null;

  const ticket = useMemo(() => (a.incidencia_id
    ? { id: a.incidencia_id, numero: a.incidencia_ref || a.incidencia_id,
        titulo: a.incidencia_titulo || a.description || "", prioridad: "0" }
    : null), [a.incidencia_id, a.incidencia_ref, a.incidencia_titulo, a.description]);

  // Las pestañas son SOLO las que esa persona puede usar: el panel de
  // incidencias es de TIC, y sin Notion conectado o sin pasos asignados esos
  // buscadores no tienen nada que ofrecer. Una pestaña que solo sirve para
  // explicar por que no sirve es una pestaña de sobra.
  //
  // Con una excepcion: la del enlace que la entrada YA tiene se muestra siempre,
  // aunque no se tenga acceso. Si no, una entrada de incidencia vista por quien
  // no es de TIC —o una de Notion despues de desconectar Notion— esconderia su
  // propio enlace, y no habria forma ni de verlo ni de quitarlo.
  const disponibles = useMemo(() => TIPOS_USO.filter(t =>
    t.key === enlazado
    || t.key === "tarea"
    || (t.key === "paso"       && misPasos.length)
    || (t.key === "notion"     && notionItems.length)
    || (t.key === "incidencia" && verIncidencias)
    // La pestaña de reuniones se ofrece siempre: el propio buscador dice si el
    // calendario esta vacio o sin conectar, y a diferencia de los otros aqui no
    // hay una lista en memoria con la que saberlo de antemano.
    || t.key === "outlook"
  ), [enlazado, misPasos.length, notionItems.length, verIncidencias, unico]); // eslint-disable-line

  // Sin enlace no hay nada que deducir, asi que la pestaña abierta es estado de
  // pantalla: la del enlace si lo hay, y la primera disponible si no. Asi el
  // buscador esta SIEMPRE a la vista y asignar es escribir, no dos clics.
  const [abierta, setAbierta] = useState(null);
  const origenInicialUso = unico && (origenInicial === "paso" || origenInicial === "notion") ? "tarea" : origenInicial;
  const inicial = origenInicialUso && disponibles.some(t => t.key === origenInicialUso) ? origenInicialUso : null;
  const tipo = enlazado || abierta || inicial || disponibles[0]?.key || null;

  // Al quitar un enlace la pestaña se queda donde estaba, en vez de saltar a la
  // primera: quien acaba de desenlazar algo casi siempre va a poner otro igual.
  useEffect(() => { if (enlazado) setAbierta(enlazado); }, [enlazado]);

  const [menu, setMenu] = useState(false);
  const caja = useRef(null), boton = useRef(null), opciones = useRef([]);
  const actual = TIPOS_USO.find(t => t.key === tipo) || null;

  // Cerrar el menu al pulsar fuera.
  useEffect(() => {
    if (!menu) return;
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [menu]);

  // Al abrir, el foco pasa a la opcion marcada (o a la primera).
  useEffect(() => {
    if (!menu) return;
    const i = Math.max(0, disponibles.findIndex(t => t.key === tipo));
    opciones.current[i]?.focus();
  }, [menu]);

  // Teclado del menu: flechas, Inicio/Fin, Escape y Tab lo cierran.
  const teclaMenu = e => {
    const n = disponibles.length;
    const i = opciones.current.findIndex(el => el === document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); opciones.current[(i + 1) % n]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); opciones.current[(i - 1 + n) % n]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); opciones.current[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); opciones.current[n - 1]?.focus(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setMenu(false); boton.current?.focus(); }
    else if (e.key === "Tab") setMenu(false);
  };

  // La tarea que tiene la entrada, para pintarla en el selector unico. Si el enlace ya no existe (un paso
  // borrado, una pagina que ya no esta) no se esconde: se dice, y se puede quitar.
  const valorTarea = useMemo(() => {
    if (!unico) return null;
    const dePool = (kind, id) => poolMisTareas.find(s => s.kind === kind && s.id === String(id));
    if (a.task_id) {
      const s = dePool("paso", a.task_id), p = misPasos.find(x => String(x.id) === String(a.task_id));
      return { kind: "paso", id: String(a.task_id), title: s?.title || p?.title || "Paso que ya no está",
               sub: s?.sub || p?.project_name || "El enlace ya no existe" };
    }
    if (a.external_task_id) {
      const s = dePool("notion", a.external_task_id), n = notionItems.find(x => x.external_id === a.external_task_id);
      return { kind: "notion", id: String(a.external_task_id), title: s?.title || n?.title || "Página que ya no está",
               sub: s?.sub || (n ? "Notion" : "El enlace ya no existe") };
    }
    if (a.mis_tarea_id) {
      const s = dePool("suelta", a.mis_tarea_id);
      return { kind: "suelta", id: String(a.mis_tarea_id), title: s?.title || "Tarea enviada", sub: s?.sub || "Tarea enviada" };
    }
    return null;
  }, [unico, poolMisTareas, misPasos, notionItems, a.task_id, a.external_task_id, a.mis_tarea_id]);

  // Elegir (o quitar) la tarea: un solo enlace por entrada, los otros dos se sueltan.
  const elegirTarea = s => {
    if (!s) return onChange({ ...SIN_TAREA });
    if (s.kind === "paso") onChange({ ...SOLO.tarea, ...SIN_TAREA, task_id: s.id, project_id: s.project_id });
    else if (s.kind === "notion") onChange({ ...SOLO.tarea, ...SIN_TAREA, external_task_id: s.id, external_project_id: s.parent });
    else onChange({ ...SOLO.tarea, ...SIN_TAREA, mis_tarea_id: s.id });
  };

  const cambiarTipo = k => {
    setAbierta(k);
    // Solo se escribe si habia un enlace que quitar. Cambiar de pestaña con el
    // campo vacio no tiene por que guardar nada.
    if (enlazado && k !== enlazado) onChange(SOLO[k]);
  };

  return (
    <div id={id} role="group" aria-labelledby={labelledBy}>
      {/* El origen se elige con el icono acoplado a la izquierda del campo; el
          buscador del resto cambia con el. Un solo enlace por entrada: cambiar de
          origen quita el anterior (cambiarTipo). */}
      <div className="cw-combo" style={{ display: "flex", alignItems: "stretch", position: "relative" }}>
        {/* position static: el menu de tipos se ancla a la CAJA entera (top:100% = bajo el borde del campo), crezca lo que crezca. */}
        <div ref={caja} style={{ position: "static", flexShrink: 0, display: "flex", alignSelf: "center" }}>
          <button ref={boton} type="button" className="btn cw-combo-tp" disabled={!tipo}
            aria-haspopup="listbox" aria-expanded={menu}
            aria-label={`Tipo de asignación: ${actual?.label || "ninguno"}`} title={actual?.label}
            onClick={() => setMenu(m => !m)}
            onKeyDown={e => { if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); setMenu(true); } }}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
              minWidth: 40, minHeight: 32, padding: "0 8px", margin: "0 4px 0 5px", cursor: "pointer",
              border: "none", borderRadius: 6, background: "var(--accent-bg)", color: "var(--accent)" }}>
            {actual && <Icon name={actual.icon} size={15} color="var(--accent)" />}
            {unico && actual && <span style={{ fontSize: 12.5, fontWeight: 700, marginLeft: 3 }}>{actual.label}</span>}
            <Icon name="chevron-down" size={9} color="var(--accent)" />
          </button>
          {menu && (
            <div role="listbox" aria-label="Tipo de asignación" onKeyDown={teclaMenu}
              style={{ position: "absolute", left: 0, top: "100%", marginTop: 4, zIndex: 30, minWidth: unico ? 280 : 230, maxWidth: "calc(100vw - 32px)",
                background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
                boxShadow: "var(--shadow-md)", padding: 5 }}>
              {disponibles.map((t, i) => {
                const on = tipo === t.key;
                return (
                  <button key={t.key} type="button" role="option" aria-selected={on} className="btn"
                    ref={el => { opciones.current[i] = el; }} tabIndex={-1}
                    onClick={() => { cambiarTipo(t.key); setMenu(false); boton.current?.focus(); }}
                    style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", minHeight: unico ? 46 : 40,
                      padding: "0 10px", border: "none", borderRadius: 7, cursor: "pointer",
                      fontFamily: "var(--font)", fontSize: 13, textAlign: "left",
                      fontWeight: on ? 700 : 500,
                      background: on ? "var(--accent-bg)" : "none",
                      color: on ? "var(--accent)" : "var(--text2)" }}>
                    <Icon name={t.icon} size={14} color={on ? "var(--accent)" : "var(--text3)"} />
                    {unico && t.desc
                      ? <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }}>
                          <b style={{ fontWeight: on ? 700 : 600 }}>{t.label}</b>
                          <small style={{ fontSize: 11, fontWeight: 400, color: "var(--text3)" }}>{t.desc}</small>
                        </span>
                      : t.label}
                    {on && <span aria-hidden="true" style={{ marginLeft: "auto" }}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
      {tipo === "tarea" && (
        <SelectorTareaUnico pool={poolMisTareas} misPasos={misPasos} notionItems={notionItems} traer={false} flotante
          value={valorTarea} onChange={elegirTarea} />
      )}

      {tipo === "paso" && (
        <SelectorPaso taskId={a.task_id} pasos={misPasos}
          onChange={(taskId, projectId) =>
            onChange({ ...SOLO.paso, task_id: taskId, project_id: projectId })} />
      )}

      {tipo === "notion" && (
        <NotionSelector linkedId={a.external_task_id} notionItems={notionItems}
          onChange={(extId, extParentId) =>
            onChange({ ...SOLO.notion, external_task_id: extId,
                       external_project_id: extParentId })} />
      )}

      {tipo === "outlook" && (
        // Las reuniones que se ofrecen son las del dia que dice la entrada, no
        // las de hoy: se apunta el cuaderno de un dia que ya paso tanto como el
        // de hoy. Misma pareja de campos que usa el formulario para pintar
        // «Fecha inicio».
        <SelectorReunion eventoId={a.outlook_event_id}
          fecha={(a.start_date || a.entry_date || "").slice(0, 10)}
          onChange={e => onChange({ ...SOLO.outlook,
            outlook_event_id: e ? e.external_id : null })} />
      )}

      {tipo === "incidencia" && (a.incidencia_id ? (
        // Ya enlazada: lo que hace falta es verla y poder abrirla, no cambiarla.
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
          borderRadius: 8, border: "1px solid var(--red-line)", background: "var(--red-bg)" }}>
          <Icon name="headset" size={11} color="var(--red-text)" />
          <button className="btn" onClick={() => onAbrirIncidencia?.(a.incidencia_id)}
            title={a.incidencia_titulo ? `Abrir la incidencia en el panel: ${a.incidencia_titulo}` : "Abrir la incidencia en el panel"}
            style={{ flex: 1, textAlign: "left", background: "none", border: "none",
              cursor: "pointer", padding: 0, fontFamily: "var(--font)", fontSize: 12,
              fontWeight: 600, color: "var(--red-text)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span style={{ textDecoration: "underline" }}>#{a.incidencia_ref || a.incidencia_id}</span>
            {a.incidencia_titulo && <span style={{ fontWeight: 400 }}> · {a.incidencia_titulo}</span>}
          </button>
          <button type="button" className="btn cw-x" title="Quitar el enlace"
            aria-label={`Quitar la incidencia #${a.incidencia_ref || a.incidencia_id}`}
            onClick={() => onChange({ incidencia_id: null, incidencia_ref: null, incidencia_titulo: null })}>×</button>
        </div>
      ) : (
        <SelectorIncidencia ticket={ticket} onAbrirPanel={undefined}
          onChange={x => onChange({ ...SOLO.incidencia,
            incidencia_id: x ? x.id : null,
            incidencia_ref: x ? String(x.numero) : null,
            incidencia_titulo: x ? (x.titulo || null) : null })} />
      ))}
        </div>
      </div>

      {/* Sin ninguna fuente a mano no hay nada que ofrecer: ni pasos asignados,
          ni Notion conectado, ni acceso al panel de incidencias. */}
      {!tipo && (
        <div style={{ fontSize: 11, color: "var(--text3)", padding: "5px 0" }}>
          No hay nada a lo que asignarla: sin pasos asignados, sin Notion conectado
          y sin acceso al panel de incidencias.
        </div>
      )}
    </div>
  );
}
